import { Modal, Spin, Table, Button } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import { BankResponse } from '../../model/Bank.model';
import EmployeeService from '../../service/employee-service/Employee.service';
import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import { exportMaturityScheduleToExcel } from '../../utils/excelExport';
import DepositModal from './DepositModal';
import EditMaturityScheduleModal from './EditMaturityScheduleModal';
import { createMaturityTableColumns } from './MaturityTableColumns';
import {
  formatCardNumber,
  formatCurrency,
  getBalanceDifference,
  getRemainingAmount,
  getStatusColor,
  getStatusText,
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getTotalAmount,
} from './MaturityTableUtils';
import TransactionSubTable from './TransactionSubTable';
import WithdrawalModal from './WithdrawalModal';
import MaturityPaymentModal from './MaturityPaymentModal';
import { ReloadOutlined } from '@ant-design/icons';

interface MaturityTableProps {
  maturitySchedules: MaturityScheduleResponse[];
  loading: boolean;
  onTransactionSuccess?: () => void;
  currentEmployeeId?: number; // Add employee ID for edit/delete operations
  isAdmin?: boolean; // Chỉ admin mới thấy nút "Đánh dấu hoàn thành"
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
  onSearch?: (keyword: string) => void;
  onClearFilters?: () => void;
  banks?: BankResponse[];
}

const MaturityTable: React.FC<MaturityTableProps> = ({
  maturitySchedules,
  loading,
  onTransactionSuccess,
  currentEmployeeId = 1, // Default fallback, should be passed from parent
  isAdmin = false,
  pagination,
  onTableChange,
  onSearch,
  onClearFilters,
  banks = [],
}) => {
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedMaturitySchedule, setSelectedMaturitySchedule] =
    useState<MaturityScheduleResponse | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableKey, setTableKey] = useState<number>(0);
  const tableRef = useRef<any>(null);
  const [transactionRefreshTrigger, setTransactionRefreshTrigger] = useState(0);
  const [tableScrollX, setTableScrollX] = useState<number>(
    window.innerWidth - 180
  );

  // State for inline editing
  const [editingNotes, setEditingNotes] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [editingStatus, setEditingStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const [tempNotes, setTempNotes] = useState<{ [key: number]: string }>({});
  const [tempStatus, setTempStatus] = useState<{ [key: number]: string }>({});
  const [inlineLoading, setInlineLoading] = useState<{
    [key: number]: boolean;
  }>({});

  // Create employee names object from the enhanced data
  const employeeNames = React.useMemo(() => {
    const names: { [key: number]: string } = {};
    maturitySchedules.forEach((schedule) => {
      schedule.employees?.forEach((employee) => {
        names[employee.id] = employee.name;
      });
      // Add creator name
      if (schedule.createdBy && schedule.createdByName) {
        names[schedule.createdBy] = schedule.createdByName;
      }
    });
    return names;
  }, [maturitySchedules]);

  // Helper function to get payment amount directly from props
  const getPaymentAmount = (maturityScheduleId: number): number => {
    const schedule = maturitySchedules.find(
      (ms) => ms.id === maturityScheduleId
    );
    return schedule?.totalPaymentAmount || 0;
  };

  // Dữ liệu đã được backend sort + paginate theo business priority
  const sortedMaturitySchedules = maturitySchedules;

  // Handle expand/collapse
  const handleExpand = (
    expanded: boolean,
    record: MaturityScheduleResponse
  ) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
    }
  };

  // Check if row is expanded
  const isRowExpanded = (recordId: number) => {
    return expandedRowKeys.includes(recordId);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    // Reset expanded rows
    setExpandedRowKeys([]);

    // Reset inline editing states
    setEditingNotes({});
    setEditingStatus({});
    setTempNotes({});
    setTempStatus({});

    // Reset parent/server filters
    if (onClearFilters) {
      onClearFilters();
    } else if (onSearch) {
      onSearch('');
    }

    // Reset table state by incrementing the key
    setTableKey((prev) => prev + 1);
  };

  const handleDeposit = (record: MaturityScheduleResponse) => {
    // Kiểm tra nếu trạng thái là ABANDONED
    if (record.status === 'ABANDONED') {
      toast.warning(
        'Không thể tạo giao dịch nạp tiền cho kỳ đáo hạn đã được đánh dấu "Không làm"'
      );
      return;
    }
    setSelectedMaturitySchedule(record);
    setDepositModalVisible(true);
  };

  const handleWithdrawal = (record: MaturityScheduleResponse) => {
    // Kiểm tra nếu trạng thái là ABANDONED
    if (record.status === 'ABANDONED') {
      toast.warning(
        'Không thể tạo giao dịch rút tiền cho kỳ đáo hạn đã được đánh dấu "Không làm"'
      );
      return;
    }
    setSelectedMaturitySchedule(record);
    setWithdrawalModalVisible(true);
  };

  // Helper functions to check if deposit/withdrawal should be disabled
  const isDepositDisabled = (record: MaturityScheduleResponse) => {
    return record.status === 'ABANDONED';
  };

  const isWithdrawalDisabled = (record: MaturityScheduleResponse) => {
    return record.status === 'ABANDONED';
  };

  const handleEdit = (record: MaturityScheduleResponse) => {
    setSelectedMaturitySchedule(record);
    setEditModalVisible(true);
  };

  const handleDelete = (record: MaturityScheduleResponse) => {
    // Kiểm tra có giao dịch thực tế hay không (loại bỏ giao dịch đã bị xóa)
    const hasTransactions =
      (record.totalDepositAmount && record.totalDepositAmount > 0) ||
      (record.totalWithdrawalAmount && record.totalWithdrawalAmount > 0) ||
      (record.transactions &&
        record.transactions.some(
          (t) => t.status === 'COMPLETED' && !t.deletedAt
        ));

    Modal.confirm({
      title: 'Xác nhận xóa kỳ đáo hạn',
      content: hasTransactions
        ? `Bạn có chắc chắn muốn xóa kỳ đáo hạn này? 
           
           Kỳ đáo hạn này có giao dịch liên quan và việc xóa sẽ:
           • Xóa tất cả giao dịch liên quan (soft delete)
           • Hoàn tác số dư cho các giao dịch đã hoàn thành
           • Xóa hoàn toàn kỳ đáo hạn
           
           Hành động này không thể hoàn tác.`
        : `Bạn có chắc chắn muốn xóa kỳ đáo hạn này?
           
           Hành động này không thể hoàn tác.`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      width: 500,
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const response = await MaturityScheduleService.deleteMaturitySchedule(
            record.id,
            currentEmployeeId
          );

          if (response.success) {
            toast.success('Xóa kỳ đáo hạn thành công');
            if (onTransactionSuccess) {
              onTransactionSuccess();
            }
          } else {
            toast.error(response.message || 'Có lỗi xảy ra khi xóa kỳ đáo hạn');
          }
        } catch (error: any) {
          console.error('Error deleting maturity schedule:', error);
          toast.error(
            error.response?.data?.message || 'Có lỗi xảy ra khi xóa kỳ đáo hạn'
          );
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const handleTransactionSuccess = () => {
    // Payment amounts are now provided directly by the API response
    // We just need to trigger a parent component refresh
    if (onTransactionSuccess) {
      onTransactionSuccess();
    }
    // Also trigger transaction sub-table refresh
    setTransactionRefreshTrigger((prev) => prev + 1);
  };

  // Inline editing handlers
  const handleEditNotes = (record: MaturityScheduleResponse) => {
    setEditingNotes({ ...editingNotes, [record.id]: true });
    setTempNotes({ ...tempNotes, [record.id]: record.notes || '' });
  };

  const handleSaveNotes = async (record: MaturityScheduleResponse) => {
    try {
      setInlineLoading({ ...inlineLoading, [record.id]: true });

      // Đảm bảo rằng nếu user xóa hết text thì gửi chuỗi rỗng
      const notesToSave =
        tempNotes[record.id] !== undefined ? tempNotes[record.id] : '';

      const response =
        await MaturityScheduleService.updateMaturityScheduleNotes(
          record.id,
          notesToSave,
          currentEmployeeId
        );

      if (response.success) {
        toast.success('Cập nhật ghi chú thành công');
        setEditingNotes({ ...editingNotes, [record.id]: false });

        // Clear temp notes để tránh conflict
        const newTempNotes = { ...tempNotes };
        delete newTempNotes[record.id];
        setTempNotes(newTempNotes);

        if (onTransactionSuccess) {
          onTransactionSuccess();
        }
      } else {
        toast.error(response.message || 'Có lỗi xảy ra khi cập nhật ghi chú');
      }
    } catch (error: any) {
      console.error('Error updating notes:', error);
      toast.error(
        error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật ghi chú'
      );
    } finally {
      setInlineLoading({ ...inlineLoading, [record.id]: false });
    }
  };

  const handleCancelEditNotes = (record: MaturityScheduleResponse) => {
    setEditingNotes({ ...editingNotes, [record.id]: false });

    // Clear temp notes để reset về giá trị gốc
    const newTempNotes = { ...tempNotes };
    delete newTempNotes[record.id];
    setTempNotes(newTempNotes);
  };

  const handleEditStatus = (record: MaturityScheduleResponse) => {
    setEditingStatus({ ...editingStatus, [record.id]: true });
    setTempStatus({ ...tempStatus, [record.id]: record.status });
  };

  const getStatusOptions = (record: MaturityScheduleResponse) => {
    const options = [
      { value: 'PENDING', label: 'Chưa làm' },
      { value: 'PAID', label: 'Đã nạp đáo' },
      { value: 'COMPLETED', label: 'Hoàn thành' },
      { value: 'ABANDONED', label: 'Không làm' },
    ];

    return options;
  };

  const handleSaveStatus = async (
    record: MaturityScheduleResponse,
    newStatus?: string
  ) => {
    try {
      const statusToSave = newStatus ?? tempStatus[record.id];

      if (statusToSave === 'ABANDONED') {
        const hasActualTransactions =
          (record.totalDepositAmount && record.totalDepositAmount > 0) ||
          (record.totalWithdrawalAmount && record.totalWithdrawalAmount > 0) ||
          (record.transactions &&
            record.transactions.some(
              (t) => t.status === 'COMPLETED' && !t.deletedAt
            ));

        if (hasActualTransactions) {
          toast.error(
            'Không thể đổi trạng thái "Không làm" vì đã thực hiện giao dịch'
          );
          setEditingStatus({ ...editingStatus, [record.id]: false });
          setTempStatus({ ...tempStatus, [record.id]: record.status });
          return;
        }
      }

      setInlineLoading({ ...inlineLoading, [record.id]: true });

      const response =
        await MaturityScheduleService.updateMaturityScheduleStatus(
          record.id,
          statusToSave,
          currentEmployeeId
        );

      if (response.success) {
        toast.success('Cập nhật trạng thái thành công');
        setEditingStatus({ ...editingStatus, [record.id]: false });
        if (onTransactionSuccess) {
          onTransactionSuccess();
        }
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
    } finally {
      setInlineLoading({ ...inlineLoading, [record.id]: false });
    }
  };

  const handleCancelEditStatus = (record: MaturityScheduleResponse) => {
    setEditingStatus({ ...editingStatus, [record.id]: false });
    setTempStatus({ ...tempStatus, [record.id]: record.status });
  };

  const handleToggleCompletion = async (record: MaturityScheduleResponse) => {
    try {
      setInlineLoading({ ...inlineLoading, [record.id]: true });

      const response = await MaturityScheduleService.toggleCompletionStatus(
        record.id,
        currentEmployeeId
      );

      if (response.success) {
        toast.success(response.message || 'Đã cập nhật trạng thái hoàn thành');
        if (onTransactionSuccess) {
          onTransactionSuccess();
        }
      } else {
        toast.error(
          response.message || 'Có lỗi xảy ra khi cập nhật trạng thái hoàn thành'
        );
      }
    } catch (error: any) {
      console.error('Error toggling completion status:', error);
      toast.error(
        error.response?.data?.message ||
        'Có lỗi xảy ra khi cập nhật trạng thái hoàn thành'
      );
    } finally {
      setInlineLoading({ ...inlineLoading, [record.id]: false });
    }
  };

  const handleExportExcel = async (record: MaturityScheduleResponse) => {
    try {
      await exportMaturityScheduleToExcel(record);
      toast.success('Xuất hóa đơn Excel thành công');
    } catch (error: any) {
      console.error('Error exporting Excel:', error);
      toast.error('Có lỗi xảy ra khi xuất hóa đơn Excel');
    }
  };

  const handleRecordPayment = (record: MaturityScheduleResponse) => {
    // Kiểm tra nếu trạng thái là ABANDONED
    if (record.status === 'ABANDONED') {
      toast.warning(
        'Không thể ghi nhận thanh toán cho kỳ đáo hạn đã được đánh dấu "Không làm"'
      );
      return;
    }
    setSelectedMaturitySchedule(record);
    setPaymentModalVisible(true);
  };

  // Create columns using the helper function
  const columns = createMaturityTableColumns({
    employeeNames,
    maturitySchedules,
    formatCurrency,
    formatCardNumber,
    getStatusColor,
    getStatusText,
    getTotalDepositAmount,
    getTotalWithdrawalAmount,
    getRemainingAmount,
    getBalanceDifference,
    getTotalAmount,
    getPaymentAmount,
    handleDeposit,
    handleWithdrawal,
    handleExpand,
    isRowExpanded,
    handleEdit,
    handleDelete,
    handleToggleCompletion,
    handleExportExcel,
    handleRecordPayment,
    // Inline editing props
    editingNotes,
    editingStatus,
    tempNotes,
    tempStatus,
    inlineLoading,
    handleEditNotes,
    handleSaveNotes,
    handleCancelEditNotes,
    handleEditStatus,
    handleSaveStatus,
    handleCancelEditStatus,
    setTempNotes,
    setTempStatus,
    getStatusOptions,
    // Disable functions
    isDepositDisabled,
    isWithdrawalDisabled,
    isAdmin,
    onSearch,
    banks,
  });

  useEffect(() => {
    const handleResize = () => {
      setTableScrollX(window.innerWidth - 180);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const modalBodyRef = React.useRef<HTMLDivElement>(null);
  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button
          icon={<ReloadOutlined />}
          onClick={handleClearAllFilters}
          type="text"
          size="small"
          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 border-0 shadow-none transition-all duration-200 flex items-center gap-1"
          title="Xóa tất cả bộ lọc"
        >
          <span className="text-xs">Xoá bộ lọc</span>
        </Button>
      </div>
      <Spin spinning={loading || deleteLoading}>
      <div
        ref={modalBodyRef}
        style={{ maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}
      >
        <Table
          key={tableKey}
          ref={tableRef}
          columns={columns}
          dataSource={sortedMaturitySchedules}
          rowKey="id"
          pagination={{
            current: pagination?.current,
            pageSize: pagination?.pageSize,
            total: pagination?.total,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} kỳ đáo hạn`,
            showSizeChanger: true,
          }}
          onChange={onTableChange}
          scroll={{ x: tableScrollX }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          className="bg-white rounded-lg"
          size="small"
          rowClassName="group"
          expandable={{
            expandedRowRender: (record) => (
              <TransactionSubTable
                maturityScheduleId={record.id}
                onTransactionUpdate={handleTransactionSuccess}
                refreshTrigger={transactionRefreshTrigger}
                isAdmin={isAdmin}
              />
            ),
            rowExpandable: (record) => true, // All rows can be expanded
            expandRowByClick: false, // Only expand by clicking the expand icon
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            showExpandColumn: false, // Hide default expand column
          }}
        />
      </div>
      </Spin>

      {/* Deposit Modal */}
      {selectedMaturitySchedule && (
        <DepositModal
          visible={depositModalVisible}
          onClose={() => {
            setDepositModalVisible(false);
            setSelectedMaturitySchedule(null);
          }}
          onSuccess={handleTransactionSuccess}
          maturitySchedule={selectedMaturitySchedule}
        />
      )}

      {/* Withdrawal Modal */}
      {selectedMaturitySchedule && (
        <WithdrawalModal
          visible={withdrawalModalVisible}
          onClose={() => {
            setWithdrawalModalVisible(false);
            setSelectedMaturitySchedule(null);
          }}
          onSuccess={handleTransactionSuccess}
          maturitySchedule={selectedMaturitySchedule}
        />
      )}

      {/* Edit Modal */}
      {selectedMaturitySchedule && (
        <EditMaturityScheduleModal
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedMaturitySchedule(null);
          }}
          onSuccess={handleTransactionSuccess}
          maturitySchedule={selectedMaturitySchedule}
          employeeId={currentEmployeeId}
        />
      )}

      {/* Payment Modal */}
      {selectedMaturitySchedule && (
        <MaturityPaymentModal
          visible={paymentModalVisible}
          onCancel={() => {
            setPaymentModalVisible(false);
            setSelectedMaturitySchedule(null);
          }}
          onSuccess={handleTransactionSuccess}
          maturitySchedule={selectedMaturitySchedule}
        />
      )}
    </>
  );
};

export default MaturityTable;
